<template>
    <div ref="sliderRef" class="tw-w-full tw-flex tw-select-none">
        <div
            v-for="split in splits"
            :key="split"
            :class="[
                'tw-relative tw-h-3',
                getColour(split - 1),
                {
                    'tw-rounded-l-md': split === 1,
                    'tw-rounded-r-md': split === splits,
                },
            ]"
            :style="`width: ${model[split - 1]}%;`"
        >
            <!-- Percentage display -->
            <div
                class="tw-absolute tw-flex tw-top-0 tw-left-0 tw-right-0 tw-bottom-0 tw-items-center tw-justify-center tw-text-white tw-font-medium tw-text-xs tw-overflow-hidden"
            >
                {{ (model[split - 1] ?? 0).toFixed(2) }}%
            </div>
            <!-- Handle -->
            <div
                v-if="split !== splits && !disabled"
                class="tw-w-5 tw-h-5"
                :class="[
                    'tw-absolute tw-shadow-sm -tw-right-2 -tw-top-1 tw-z-[1] tw-cursor-pointer tw-scale-[0.9] tw-border-[3px] tw-border-white tw-rounded-full hover:tw-scale-[1]',
                    getColour(split - 1),
                ]"
                @pointerdown="onHandlePointerDown($event, split - 1)"
            ></div>
        </div>
    </div>
</template>
<script setup lang="ts">
import { onBeforeUnmount, ref, useTemplateRef, watch } from 'vue';
import type { PropType } from 'vue';

const props = defineProps({
    splits: {
        type: Number,
        default: 3,
    },
    colours: {
        type: Array as PropType<Array<string>>,
        default: () => ['tw-bg-sky-400', 'tw-bg-emerald-400', 'tw-bg-violet-400'],
    },
    disabled: {
        type: Boolean,
        default: false,
    },
});

const sliderRef = useTemplateRef<HTMLDivElement | null>('sliderRef');
const activeHandleIndex = ref<number | null>(null);
const pointerStartX = ref(0);
const initialValues = ref<number[]>([]);

const model = defineModel<number[]>({
    type: Array,
    default: () => [33.33333, 33.33333, 33.33333],
});

const DECIMAL_PLACES = 5;

const applyPrecision = (values: number[]): number[] => {
    if (!values.length) {
        return [];
    }

    const rounded = values.map((value) => Number(value.toFixed(DECIMAL_PLACES)));
    const total = rounded.reduce((sum, value) => sum + value, 0);

    if (total === 0) {
        const equalShare = Number((100 / rounded.length).toFixed(DECIMAL_PLACES));
        return rounded.map(() => equalShare);
    }

    const diff = Number((100 - total).toFixed(DECIMAL_PLACES));
    rounded[rounded.length - 1] = Number(((rounded[rounded.length - 1] ?? 0) + diff).toFixed(DECIMAL_PLACES));

    return rounded;
};

const createEqualDistribution = (count: number): number[] => {
    if (count <= 0) {
        return [];
    }

    const share = 100 / count;
    const values = Array.from({ length: count }, () => share);
    return applyPrecision(values);
};

const splitLargestSegment = (values: number[]): number[] => {
    if (!values.length) {
        return values;
    }

    const largestIndex = values.reduce(
        (maxIndex, value, currentIndex, array) => (value > (array[maxIndex] ?? -Infinity) ? currentIndex : maxIndex),
        0
    );

    const updated = [...values];
    const half = (updated[largestIndex] ?? 0) / 2;
    updated[largestIndex] = half;
    updated.splice(largestIndex + 1, 0, half);

    return updated;
};

const mergeLastSegment = (values: number[]): number[] => {
    if (values.length <= 1) {
        return values;
    }

    const updated = [...values];
    const lastValue = updated.pop() ?? 0;
    updated[updated.length - 1] = (updated[updated.length - 1] ?? 0) + lastValue;

    return updated;
};

const adjustModelToSplitCount = (targetCount: number) => {
    const desiredCount = Math.max(0, Math.floor(targetCount));

    if (desiredCount === 0) {
        model.value = [];
        return;
    }

    let values = model.value.length ? [...model.value] : createEqualDistribution(desiredCount);

    while (values.length < desiredCount) {
        values = splitLargestSegment(values);
    }

    while (values.length > desiredCount) {
        values = mergeLastSegment(values);
    }

    model.value = applyPrecision(values);
};

// Keep the percentages in sync with the latest split count
watch(
    () => props.splits,
    (newSplits) => {
        adjustModelToSplitCount(newSplits);
    },
    { immediate: true }
);

watch(
    () => model.value,
    (newValue) => {
        if (newValue.length !== props.splits) {
            adjustModelToSplitCount(props.splits);
        }
    },
    { deep: true }
);

const clamp = (value: number, min: number, max: number) => {
    if (value < min) {
        return min;
    }

    if (value > max) {
        return max;
    }

    return value;
};

const updateSplitValues = (index: number, leftValue: number) => {
    const nextValues = [...model.value];
    const pairTotal = (initialValues.value[index] ?? 0) + (initialValues.value[index + 1] ?? 0);
    const newLeft = clamp(leftValue, 0, pairTotal);
    const newRight = pairTotal - newLeft;

    nextValues[index] = Number(newLeft.toFixed(5));
    nextValues[index + 1] = Number(newRight.toFixed(5));
    model.value = nextValues;
};

const onHandlePointerMove = (event: PointerEvent) => {
    if (activeHandleIndex.value === null || !sliderRef.value) {
        return;
    }

    const bounds = sliderRef.value.getBoundingClientRect();
    if (bounds.width === 0) {
        return;
    }

    const deltaPercent = ((event.clientX - pointerStartX.value) / bounds.width) * 100;
    const newLeft = (initialValues.value[activeHandleIndex.value] ?? 0) + deltaPercent;
    updateSplitValues(activeHandleIndex.value, newLeft);
};

const stopDragging = () => {
    window.removeEventListener('pointermove', onHandlePointerMove);
    window.removeEventListener('pointerup', stopDragging);
    activeHandleIndex.value = null;
};

const onHandlePointerDown = (event: PointerEvent, index: number) => {
    if (model.value[index] === undefined || model.value[index + 1] === undefined) {
        return;
    }

    event.preventDefault();
    activeHandleIndex.value = index;
    pointerStartX.value = event.clientX;
    initialValues.value = model.value.map((value) => Number(value));

    window.addEventListener('pointermove', onHandlePointerMove);
    window.addEventListener('pointerup', stopDragging);
};

const getColour = (index: number): string => {
    return props.colours[index] || 'tw-bg-gray-400';
};

onBeforeUnmount(() => {
    stopDragging();
});
</script>
